/**
 * Member credit-pack prioritization + eligibility, ported from the customer
 * app's PaymentOptionsBuilder so the embed auto-applies the *same* credit the
 * customer app would pick.
 *
 * Priority (which credit to use first): finite credits by soonest expiration,
 * then no-expiration credits by creation time and stable credit ID. Eligibility
 * mirrors the customer app's `disabled` guard — a credit is only auto-applied
 * when it is genuinely usable for this event, so we never auto-pick something
 * the server's registration guardrail would 400.
 */

/**
 * The per-product credit option as returned by the authed registration-context
 * (`EventRegistrationPaymentOptionsDto.punchCards`). Only the fields that drive
 * prioritization/eligibility are modeled here.
 */
export interface MemberCreditOption {
  /** Zipper product id — passed back as `consumeCreditForProductId`. */
  productId: string;
  productName?: string;
  isUnlimited?: boolean;
  numberOfUsesRemaining: number;
  /** Present when a redeemable credit is actually attached. */
  hasCredit: boolean;
  isAvailableForUse?: boolean;
  /** Exact owned allotment, distinct from other groups of this product. */
  punchCardId?: string;
  creditId?: string;
  creditCreated?: string | null;
  creditExpirationDate?: string | null;
  creditNeverExpires?: boolean;
  isWithinCreditExpiration?: boolean;
  preventBookingOutsideCreditExpiration?: boolean;
  isWithinBillingPeriod?: boolean;
  preventBookingOutsideSubscriptionWindow?: boolean;
  preventBookingOutsideContractWindow?: boolean;
  isPaused?: boolean;
  isBookAheadCredit?: boolean;
  isBookAheadEligible?: boolean;
  guestsOnly?: boolean;
  /**
   * SET-19 / CHK-20: product-level sharing gates (from the credit's product).
   * `disableFamilySharing` blocks using this credit for a family-group member;
   * `disableGuestSharing` blocks using it for a guest. When `disableGuestSharing`
   * is absent, the guest path falls back to `disableFamilySharing` (matches the
   * API's backward-compat semantics — never loosens an existing restriction).
   */
  disableFamilySharing?: boolean;
  disableGuestSharing?: boolean;
}

const timestampOf = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

const expirationOrderOf = (credit: MemberCreditOption): number => {
  if (credit.creditNeverExpires) return 1;
  return timestampOf(credit.creditExpirationDate) === null ? 2 : 0;
};

const comparisonTimeOf = (credit: MemberCreditOption): number => {
  const value = credit.creditNeverExpires
    ? credit.creditCreated
    : credit.creditExpirationDate;
  return timestampOf(value) ?? Number.POSITIVE_INFINITY;
};

/**
 * Customer-app priority order: finite expiration first, then permanent credits
 * by creation time and stable ID. Remaining pack balance is deliberately not a
 * tie-breaker: it must not make native choose a different concrete credit than
 * the server's credit-level order. Returns a new array (does not mutate input).
 */
export function sortMemberCredits(
  credits: MemberCreditOption[],
): MemberCreditOption[] {
  return [...credits].sort((a, b) => {
    const orderDifference = expirationOrderOf(a) - expirationOrderOf(b);
    if (orderDifference !== 0) return orderDifference;
    const timeDifference = comparisonTimeOf(a) - comparisonTimeOf(b);
    if (timeDifference !== 0) return timeDifference;
    const firstId = a.creditId || a.productId;
    const secondId = b.creditId || b.productId;
    return firstId.localeCompare(secondId);
  });
}

/**
 * Whether a credit is usable for this event — mirrors the customer app's
 * `disabled` guard (inverted). We conservatively exclude book-ahead / pending
 * renewal credits and guests-only packs from auto-apply.
 */
export function isMemberCreditUsable(
  pc: MemberCreditOption,
  relationship: "self" | "family" | "guest" = "self",
): boolean {
  if (pc.isAvailableForUse === false) return false;
  if (!pc.hasCredit) return false;

  const hasNoCredits = !pc.isUnlimited && pc.numberOfUsesRemaining === 0;
  if (hasNoCredits) return false;

  const isBillingRestricted =
    (!!pc.preventBookingOutsideSubscriptionWindow ||
      !!pc.preventBookingOutsideContractWindow) &&
    !pc.isWithinBillingPeriod;
  if (isBillingRestricted) return false;

  const isCreditExpired =
    !pc.creditNeverExpires &&
    !!pc.preventBookingOutsideCreditExpiration &&
    !pc.isWithinCreditExpiration;
  if (isCreditExpired) return false;

  if (pc.isPaused) return false;
  // Book-ahead / pending-renewal credits have their own steering in the app;
  // don't auto-apply them here.
  if (pc.isBookAheadCredit) return false;
  if (pc.guestsOnly && relationship !== "guest") return false;

  return true;
}

/**
 * SET-19 / CHK-20: whether a signed-in member's OWN credit may be applied to a
 * booking for the given beneficiary relationship. Family-group members are
 * gated by `disableFamilySharing`; guests by `disableGuestSharing` (falling back
 * to `disableFamilySharing` when the guest flag is absent, so existing products
 * that disabled the single legacy flag keep blocking guests — never loosened).
 * `self` (the member booking for themselves) is always allowed.
 *
 * NOTE: for a beneficiary who is themselves a signed-in member, the authed
 * registration-context API also withholds cross-owner shared cards server-side;
 * this client gate additionally covers the case where the buyer applies their
 * OWN credit to a family member / guest attendee (which the buyer-scoped context
 * cannot express).
 */
export function isMemberCreditShareableTo(
  pc: MemberCreditOption,
  relationship: 'self' | 'family' | 'guest',
): boolean {
  if (relationship === 'self') return true;
  if (relationship === 'family') return pc.disableFamilySharing !== true;
  // guest: fall back to the family flag when the guest flag is absent.
  const guestBlocked = pc.disableGuestSharing ?? pc.disableFamilySharing;
  return guestBlocked !== true;
}

/**
 * The credit the customer app would auto-apply: the first usable credit in
 * priority order. Returns null when the member has no usable credit.
 */
export function pickBestMemberCredit(
  credits: MemberCreditOption[],
): MemberCreditOption | null {
  return sortMemberCredits(credits).find((credit) => isMemberCreditUsable(credit)) ?? null;
}
